import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { useLabels } from 'wsm-common-data';
import { selectDepartmentById } from '../features/departments/departmentsSlice';
import { selectStaffMembersByDepartment } from '../features/staff/staffSlice';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsVertical } from '../hooks/useIsVertical';

import DeptHeading from './DeptHeading';
import VCard from './staffCard/VCard';
import HCard from './staffCard/HCard';

const DeptContainer = ({ departmentId }) => {
	const department = useSelector((state) =>
		selectDepartmentById(state, departmentId)
	);
	const staffMembers = useSelector((state) =>
		selectStaffMembersByDepartment(state, departmentId)
	);
	const { isMobile } = useIsMobile();
	const { isVertical } = useIsVertical();
	const numberActiveStaff = staffMembers.filter(
		(staffMember) => staffMember.status.toLowerCase() === 'active'
	).length;
	const labels = useLabels();

	if (!staffMembers || !staffMembers.length || !numberActiveStaff) {
		return (
			<div className="container-fluid border-top mt-1">
				<DeptHeading labelAlias={department.label} />
				<div className="text-center mt-1">
					<p className="text-muted">
						{labels.get('NO_STAFF_DATA_AVAILABLE_VERBIAGE')}
					</p>
				</div>
			</div>
		);
	}

	// Sets the number of columns for each Bootstrap viewport based on widget preferences
	// For more information, review React Bootstrap V3's documentation: https://react-bootstrap-v3.netlify.app/
	const getBootstrapColProps = () => {
		if (isVertical) {
			return {
				sm: 6,
				md: 4,
				lg: 3
			};
		} else {
			return {
				sm: 6,
				lg: 4
			};
		}
	};

	return (
		<div className="container-fluid border-top mt-5">
			<DeptHeading labelAlias={department.label} />
			{/* 
				Making Bootstrap's <Row /> component a 'flex container that wraps' ensures 
				responsive design when resizing to smaller viewports
			*/}
			<Row className="show-grid d-flex flex-wrap">
				{staffMembers
					.filter(
						(staffMember) =>
							staffMember.status.toLowerCase() === 'active'
					)
					.map((staffMember, i) => {
						const key = `${staffMember.email}${i}`;
						// Delay loading images for staff members after the first one on mobile
						// and after the third one on desktop
						const delayLoadImage =
							(isMobile && i > 0) || (!isMobile && i > 3);
						return (
							<Col
								// Use the spread operator on the object to set props returned from the 'getBootstrapColProps' function
								{...getBootstrapColProps()}
								// 'col-sm-full-width' ensures the column width is 100% ONLY on smaller viewports, aka mobile
								className="col-sm-full-width d-flex justify-content-center mb-5"
								key={key}
							>
								{isVertical ? (
									<VCard
										staffMember={staffMember}
										delayLoadImage={delayLoadImage}
										key={key}
									/>
								) : (
									<HCard
										staffMember={staffMember}
										delayLoadImage={delayLoadImage}
										key={key}
									/>
								)}
							</Col>
						);
					})}
			</Row>
		</div>
	);
};

DeptContainer.propTypes = {
	departmentId: PropTypes.string.isRequired
};

export default DeptContainer;
